import { InfiniteData, useMutation, useQueryClient } from 'react-query';

import { HttpMethod } from '@api/enums';
import { createHttpClient } from '@api/http-client';
import { END_POINTS } from '@constants/api-endpoints';
import { QUERY_KEYS } from '@constants/query-keys';
import { AlertType } from '@enums/alert';
import { showAlert } from '@lib/alert/alert-manager';

import { LabelItem, LabelModalData, LabelsResponseType } from '../types';

type CreateLabelPayload = LabelModalData;

export const useCreateLabel = () => {
  const queryClient = useQueryClient();
  const client = createHttpClient();

  const updateCachedLabels = (createdLabel: LabelItem) => {
    const queriesData = queryClient.getQueriesData({
      predicate: query =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === QUERY_KEYS.LABELS.LABEL_LIST,
    });

    queriesData.forEach(([queryKey]) => {
      queryClient.setQueryData(
        queryKey,
        (prevData?: InfiniteData<LabelsResponseType>) => {
          if (!prevData) {
            return {
              pages: [{ items: [createdLabel] }],
              pageParams: [],
            };
          }

          return {
            ...prevData,
            pages: prevData.pages.map((page, index) => {
              if (index === 0) {
                return {
                  ...page,
                  items: [createdLabel, ...page.items],
                };
              }

              return page;
            }),
          };
        },
      );
    });
  };

  const mutation = useMutation(
    (payload: CreateLabelPayload) => {
      return client<LabelItem>({
        method: HttpMethod.POST,
        url: END_POINTS.LABELS.CREATE_LABEL,
        data: payload,
      });
    },
    {
      retry: 0,
      onSuccess: (createdLabel: LabelItem) => {
        showAlert({
          type: AlertType.SUCCESS,
          message: 'Label created successfully',
        });

        updateCachedLabels(createdLabel);
      },
      onError: (error: any) => {
        showAlert({
          type: AlertType.ERROR,
          message: error.message || 'Failed to create label',
        });
      },
    },
  );

  return mutation;
};
